import Icon from 'components/Icon';
import * as consts from 'consts/consts';
import { FC, useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Nav, NavItem, NavLink } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { useFeatures } from 'utils/util/features';
import Page from 'views/Layout/Pages/Page';
import CustomSurveyBasicsTab from './CustomSurveyBasicsTab';
import {
  type CustomSurveyData,
  SURVEY_EDITOR_DEFAULT_TAB,
  SURVEY_EDITOR_TAB_PARAM,
  SURVEY_EDITOR_TABS,
  type SurveyEditorTab,
} from './consts';
import CustomSurveyReportsTab from './Reports/CustomSurveyReportsTab';

interface RouteParams {
  id: string;
}

// The survey's configuration area: the survey itself (Basics) and its reports,
// as sibling tabs. Holds the header, the tab selection, and the survey both
// the header and the Basics tab read from.
const CustomSurveyConfigurationPage: FC = () => {
  const { id } = useParams<RouteParams>();
  const { formatMessage } = useIntl();
  const history = useHistory();
  const location = useLocation();
  const { isGroupAndOptionEnabled } = useFeatures();
  const reportsEnabled = isGroupAndOptionEnabled(
    'custom_surveys',
    'custom_survey_reports'
  );

  // Fetched once here and handed to the Basics tab, which would otherwise
  // request the same survey again - useConfirmApi neither caches nor dedupes.
  // Kept in state rather than read straight off the hook so a save or status
  // change inside the tab can hand back the updated survey.
  const [survey, setSurvey] = useState<CustomSurveyData | null>(null);

  const surveyCallback = useCallback(
    (data: CustomSurveyData | null, error: unknown, hardError: unknown) => {
      if (!data || error || hardError) return;
      setSurvey(data);
    },
    []
  );

  const { status: surveyStatus } = useConfirmApi<CustomSurveyData>({
    method: 'GET',
    url: `custom-survey/surveys/${id}`,
    callback: surveyCallback,
  });

  // Kept in the URL (`?tab=`) rather than local state so the Reports tab is
  // directly linkable; falls back to the default if reports isn't enabled.
  const tabParam = new URLSearchParams(location.search).get(
    SURVEY_EDITOR_TAB_PARAM
  );

  const tab: SurveyEditorTab =
    tabParam === 'reports' && !reportsEnabled
      ? SURVEY_EDITOR_DEFAULT_TAB
      : (SURVEY_EDITOR_TABS.find((candidate) => candidate === tabParam) ??
        SURVEY_EDITOR_DEFAULT_TAB);

  // Leaving Basics unmounts its form, so both the tab switch and Back have to
  // clear its unsaved-changes guard first.
  const leaveGuardRef = useRef<(() => boolean) | null>(null);
  const canLeaveBasics = useCallback(
    () => leaveGuardRef.current?.() ?? true,
    []
  );

  const setTab = useCallback(
    (next: SurveyEditorTab) => {
      if (!canLeaveBasics()) return;
      const params = new URLSearchParams(location.search);
      params.set(SURVEY_EDITOR_TAB_PARAM, next);
      history.replace({ search: params.toString() });
    },
    [canLeaveBasics, history, location.search]
  );

  const handleBack = useCallback(() => {
    if (!canLeaveBasics()) return;
    history.push(consts.ADMINISTRATION_CUSTOM_SURVEYS(formatMessage).path);
  }, [canLeaveBasics, history, formatMessage]);

  return (
    <Page plainTextTitle={survey?.name ?? ''}>
      <>
        <div className="position-relative d-flex align-items-center mt-4 mb-4">
          <Button color="link" onClick={handleBack} className={'ps-0'}>
            <Icon className="me-1" icon={consts.ICONS.ARROW_LEFT} />
            <FormattedMessage
              id="app.views.administration.custom_surveys.configuration.back"
              defaultMessage="Back to surveys"
            />
          </Button>
          <h2 className="mb-0 position-absolute start-50 translate-middle-x">
            {survey?.name ?? ''}
          </h2>
        </div>

        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              href="#"
              active={tab === 'basics'}
              onClick={(e) => {
                e.preventDefault();
                setTab('basics');
              }}
            >
              <FormattedMessage
                id="app.views.administration.custom_surveys.configuration.tab.basics"
                defaultMessage="Basics"
              />
            </NavLink>
          </NavItem>
          {reportsEnabled && (
            <NavItem>
              <NavLink
                href="#"
                active={tab === 'reports'}
                onClick={(e) => {
                  e.preventDefault();
                  setTab('reports');
                }}
              >
                <FormattedMessage
                  id="app.views.administration.custom_surveys.configuration.tab.reports"
                  defaultMessage="Reports"
                />
              </NavLink>
            </NavItem>
          )}
        </Nav>

        {tab === 'basics' && (
          <CustomSurveyBasicsTab
            survey={survey}
            isLoading={surveyStatus === 'LOADING'}
            onSurveyChange={setSurvey}
            leaveGuardRef={leaveGuardRef}
          />
        )}

        {tab === 'reports' && reportsEnabled && (
          <CustomSurveyReportsTab surveyId={Number(id)} />
        )}
      </>
    </Page>
  );
};

export default CustomSurveyConfigurationPage;
